/**
 * Полоса действий экрана черновика и блок «что известно» над ней.
 *
 * ПОЛОСА ЗАКРЕПЛЕНА, А НЕ ПРОСТО СТОИТ ВНИЗУ. Прокручивается вся страница, а не панель
 * (см. `.panel` в DraftPanel.module.css — почему), поэтому `position: sticky; bottom: 0`
 * прилипает к нижнему краю ОКНА. Область прилипания — весь `.panel`, то есть от шапки до
 * конца документа: кнопка утверждения видна и на первом экране длинного поста, и на
 * последнем. Два условия, которые нельзя нарушить, и оба уже выполнены:
 *   • между полосой и окном не должно быть ни одного scroll-контейнера — `html`/`body`
 *     несут `overflow-x: clip`, а `clip` scroll-контейнером НЕ делает (это и есть причина,
 *     по которой в base.css выбран `clip`, а не `hidden`);
 *   • полоса — прямой ребёнок `.panel`, а не обёртки внутри него: иначе областью прилипания
 *     стала бы обёртка, и полоса ушла бы за нижний край вместе с ней.
 *
 * `--screen-pad` задаёт `DraftPanel` — это горизонтальный отступ его прокручиваемой области.
 * Полоса гасит его отрицательным полем и возвращает своим паддингом: так она идёт от края до
 * края и читается как подвал экрана, а не как ещё одна карточка. Запасное значение 0 нужно
 * для случая, когда панель решений отрисована вне этого экрана (тесты, будущие места).
 */

.context {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.bar {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin: 0 calc(-1 * var(--screen-pad, 0px));
  padding: 12px var(--screen-pad, 0px) 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--white);
  border-top: 1px solid var(--border-2);
  /* Тень вверх, а не рамка потолще: она отделяет полосу от текста, который под неё уезжает,
     и исчезает сама, когда уезжать нечему. */
  box-shadow: var(--sh-up);
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}

/* Полоса идёт во всю ширину рабочей поверхности (правка заказчика 26.08): в столбце 680px
   шесть кнопок переносились на второй ряд, и решение выглядело двумя разными наборами.
   Ширину даёт панель — полоса бьётся в её края отрицательным полем, и в один ряд кнопки
   встают сами, без изменения их порядка. */

/* Слева — действия над постом: режим экрана и удаление. Оба не имеют права стоять рядом с
   утверждением, и удаление — в первую очередь: необратимое у того же края, к которому рука
   идёт за primary, рано или поздно нажимают не глядя. */
.lead {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

/* Волосяная черта между режимами экрана и удалением. Кнопка стоит в одной группе с
   «Редактировать» по просьбе заказчика (26.08), но режимом не является: без разделителя она
   читалась бы четвёртым режимом просмотра. Красный контур говорит то же самое цветом, черта —
   расположением; вместе они стоят дешевле, чем объясняющая подпись. */
.sep {
  width: 1px;
  align-self: stretch;
  min-height: 20px;
  margin-inline: 2px;
  background: var(--border-2);
}

.gap {
  flex: 1;
  min-width: 0;
}

/* Решения — справа, primary последним: край, к которому идёт рука, один и тот же всегда. */
.acts {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Мета-строка тестовой отправки «в тесте 12:04 МСК · открыть» (docs/release-2026-09-19.md
   §9.1) — данные о версии, а не решение: стоит в ряду полосы перед группой решений, мелким
   `.meta`, чтобы не спорить с кнопками. Подробности (все отправки, причины отказов) — в `title`. */
.testMeta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  cursor: default;
}

/* Неудача — цветом статуса ошибки, как у остальных отказов на экране; причина — в подсказке. */
.testFail {
  color: var(--red-ink);
}

/* Пост ушёл, но Telegram показал его не так, как он подписан: цветом предупреждения — пост в
   канале есть, и он такой же, каким был бы в боевом. Чем разошлось — в подсказке. */
.testWarn {
  color: var(--amber-ink);
}

/* «Запостить в тестовый канал» в узкой панели — «В тестовый канал». Четыре кнопки решения с
   полным именем просят около 690px: уже этого «Утвердить…» уезжала одна во второй ряд (1024 с
   открытым бэклогом), а на телефоне кнопка занимала отдельный ряд, и полоса съедала треть
   экрана (снимки 19.09.2026). Порог — по ширине ПАНЕЛИ (контейнер `draftpanel`,
   DraftPanel.module.css), а не окна: свёрнутый бэклог даёт панели место, окно не меняется.
   Короткое имя — генерируемым текстом, а не вторым узлом: текст кнопки в DOM остаётся одним,
   имя для клавиатуры и скринридера — `aria-label`. */
@container draftpanel (max-width: 759px) {
  .testSendLong {
    display: none;
  }
  .testSend::after {
    content: "В тестовый канал";
  }
  /* И поля кнопок решения на 4px с каждой стороны уже: при 1024 с открытым бэклогом ряд с
     коротким именем недобирал до одной строки 7px, и «Утвердить…» снова уезжала одна. */
  .acts :global(.btn) {
    padding-inline: 16px;
  }
}

/* Отправка идёт — дышащая точка, как у хода прогона в шапке. */
.testDot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--violet);
  animation: testBreath 1.2s var(--ease) infinite alternate;
}

@keyframes testBreath {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .testDot {
    animation: none;
  }
}
